import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import {
  AdminClientPatient,
  DashboardAttentionEngagementClient,
} from "../api/contracts";
import {
  ClinicianPortalApiError,
  clinicianPortalApi,
} from "../api/clinicianPortalApi";
import {
  BooleanIndicator,
  PortalDataTable,
  PortalTableColumn,
  StatusPill,
  SudsPill,
  ValuePillList,
} from "../components/DataDisplay";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import {
  ClientListFilters,
  ClientSignalFilter,
  initialClientSignalFilter,
} from "../components/ClientListFilters";
import { EngagementSignal } from "../components/EngagementSignal";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { usePortalData } from "../hooks/usePortalData";
import {
  replaceWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";

type ClientFilter = "activated" | "all";

const unassignedFilterValue = "__unassigned__";

function UnassignedValue() {
  return <span className="cp-empty-value">Unassigned</span>;
}

function isAdminRequired(error: unknown): boolean {
  return (
    error instanceof ClinicianPortalApiError && error.code === "ADMIN_REQUIRED"
  );
}

export function AdminClientsPage({ clinicianCode }: { clinicianCode: string }) {
  const [filter, setFilter] = useState<ClientFilter>("activated");
  const [search, setSearch] = useState("");
  const [signalFilter, setSignalFilter] = useState<ClientSignalFilter>(
    initialClientSignalFilter,
  );
  const [clinicianFilter, setClinicianFilter] = useState("");
  const [clinicFilter, setClinicFilter] = useState("");

  const currentClinicianRequest = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getMyClients(clinicianCode, signal),
    [clinicianCode],
  );
  const currentClinicianState = usePortalData(currentClinicianRequest);
  const currentClinicianData =
    currentClinicianState.status === "success" ||
    currentClinicianState.status === "refreshing"
      ? currentClinicianState.data
      : undefined;
  const currentRole = currentClinicianData?.therapist?.role
    .trim()
    .toLocaleLowerCase();
  const adminConfirmed = currentRole === "admin";

  useEffect(() => {
    if (!currentClinicianData || adminConfirmed) return;
    replaceWithinPortal(withClinicianCode("/clinician-portal", clinicianCode));
  }, [adminConfirmed, clinicianCode, currentClinicianData]);

  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getAllClients(clinicianCode, signal),
    [clinicianCode],
  );
  const state = usePortalData(request, { enabled: adminConfirmed });
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;

  const engagementRequest = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getDashboardAttention(clinicianCode, signal),
    [clinicianCode],
  );
  const engagementState = usePortalData(engagementRequest, {
    enabled: adminConfirmed && Boolean(data),
  });
  const engagementData =
    engagementState.status === "success" ||
    engagementState.status === "refreshing"
      ? engagementState.data
      : undefined;
  const engagementByClientId = useMemo(
    () =>
      new Map<string, DashboardAttentionEngagementClient>(
        (engagementData?.engagementClients ?? []).map((client) => [
          client.clientId,
          client,
        ]),
      ),
    [engagementData],
  );

  const columns = useMemo<readonly PortalTableColumn<AdminClientPatient>[]>(
    () => [
      {
        key: "signal",
        header: "Signal",
        initialWidth: 76,
        minWidth: 76,
        resizable: false,
        cellClassName: "cp-table-cell--signal",
        render: (patient) => (
          <EngagementSignal
            client={engagementByClientId.get(patient.id)}
            unavailableLabel={
              patient.status.trim().toLowerCase() === "activated"
                ? "Engagement signal unavailable"
                : "Not evaluated — client is not activated"
            }
          />
        ),
      },
      {
        key: "accessCode",
        header: "Access Code",
        initialWidth: 110,
        minWidth: 110,
        render: (patient) => (
          <span className="cp-homework-name">{patient.accessCode}</span>
        ),
      },
      {
        key: "nickname",
        header: "Nickname",
        initialWidth: 150,
        minWidth: 120,
        render: (patient) => patient.nickname,
      },
      {
        key: "clinicianName",
        header: "Clinician",
        initialWidth: 190,
        minWidth: 150,
        render: (patient) => {
          const clinicians = patient.clinicians.length
            ? patient.clinicians
            : patient.clinician
              ? [patient.clinician]
              : [];

          if (!clinicians.length) {
            return patient.clinicianName.trim() || <UnassignedValue />;
          }

          const db = new URLSearchParams(window.location.search).get("db");

          return clinicians.map((clinician, index) => {
            const name =
              clinician.therapistName.trim() || "Assigned clinician";
            const code = clinician.clinicianCode.trim();
            const search = new URLSearchParams();
            if (db) search.set("db", db);
            search.set("clinicianCode", code);

            return (
              <React.Fragment key={clinician.id}>
                {index > 0 ? ", " : null}
                {code ? (
                  <a
                    className="cp-therapist-code-link"
                    href={`/clinician-portal/clients/${encodeURIComponent(patient.id)}/profile?${search.toString()}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${patient.nickname || patient.accessCode} as ${name} (opens in a new tab)`}
                  >
                    {name}
                  </a>
                ) : (
                  name
                )}
              </React.Fragment>
            );
          });
        },
      },
      {
        key: "clinicName",
        header: "Clinic",
        initialWidth: 210,
        minWidth: 160,
        render: (patient) => patient.clinicName.trim() || <UnassignedValue />,
      },
      {
        key: "status",
        header: "Status",
        initialWidth: 110,
        minWidth: 110,
        render: (patient) => <StatusPill status={patient.status} />,
      },
      {
        key: "clinicalSummary",
        header: "Clinical Summary",
        initialWidth: 300,
        minWidth: 180,
        cellClassName: "cp-table-cell--truncate",
        render: (patient) =>
          patient.clinicalSummary || (
            <span className="cp-empty-value">Not provided</span>
          ),
      },
      {
        key: "highSudsThreshold",
        header: "High SUDS",
        initialWidth: 96,
        minWidth: 96,
        render: (patient) => <SudsPill value={patient.highSudsThreshold} />,
      },
      {
        key: "selfInitiatedPermitted",
        header: "Self-initiated",
        initialWidth: 120,
        minWidth: 120,
        render: (patient) => (
          <BooleanIndicator value={patient.selfInitiatedPermitted} />
        ),
      },
      {
        key: "copingStrategies",
        header: "Coping Strategies",
        initialWidth: 260,
        minWidth: 180,
        render: (patient) => (
          <ValuePillList values={patient.copingStrategies} kind="coping" />
        ),
      },
      {
        key: "funActivities",
        header: "Fun Activities",
        initialWidth: 240,
        minWidth: 180,
        render: (patient) => (
          <ValuePillList values={patient.funActivities} kind="fun" />
        ),
      },
    ],
    [engagementByClientId],
  );

  const statusScopedPatients = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.patients;
    return data.patients.filter(
      (patient) => patient.status.trim().toLowerCase() === "activated",
    );
  }, [data, filter]);
  const clinicianOptions = useMemo(() => {
    const names = new Set<string>();
    for (const patient of data?.patients ?? []) {
      for (const name of patient.clinicianNames) {
        const normalizedName = name.trim();
        if (normalizedName) names.add(normalizedName);
      }
      if (patient.clinicianNames.length === 0) {
        const fallbackName = patient.clinicianName.trim();
        if (fallbackName) names.add(fallbackName);
      }
    }
    return [...names].sort((left, right) => left.localeCompare(right));
  }, [data]);
  const clinicOptions = useMemo(() => {
    const clinics = new Map<string, string>();
    for (const patient of data?.patients ?? []) {
      const clinicName = patient.clinicName.trim();
      if (!clinicName) continue;
      clinics.set(patient.clinicId.trim() || `name:${clinicName}`, clinicName);
    }
    return [...clinics]
      .map(([value, label]) => ({ value, label }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [data]);
  const hasUnassignedClinician = Boolean(
    data?.patients.some((patient) => !patient.clinicianName.trim()),
  );
  const hasUnassignedClinic = Boolean(
    data?.patients.some((patient) => !patient.clinicName.trim()),
  );
  const visiblePatients = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return statusScopedPatients.filter((patient) => {
      if (clinicianFilter) {
        const clinicianNames =
          patient.clinicianNames.length > 0
            ? patient.clinicianNames
            : [patient.clinicianName];
        const clinicianMatches =
          clinicianFilter === unassignedFilterValue
            ? !patient.clinicianName.trim()
            : clinicianNames.some((name) => name.trim() === clinicianFilter);
        if (!clinicianMatches) return false;
      }
      if (clinicFilter) {
        const clinicName = patient.clinicName.trim();
        const clinicMatches =
          clinicFilter === unassignedFilterValue
            ? !clinicName
            : (patient.clinicId.trim() || `name:${clinicName}`) ===
              clinicFilter;
        if (!clinicMatches) return false;
      }
      if (
        signalFilter &&
        engagementByClientId.get(patient.id)?.engagementStatus !== signalFilter
      ) {
        return false;
      }
      if (!query) return true;
      return [
        patient.nickname,
        patient.accessCode,
        patient.clinicianName,
        patient.clinicName,
      ]
        .join(" ")
        .toLocaleLowerCase()
        .includes(query);
    });
  }, [
    clinicFilter,
    clinicianFilter,
    engagementByClientId,
    search,
    signalFilter,
    statusScopedPatients,
  ]);

  const secondaryFiltersActive = Boolean(
    search.trim() || signalFilter || clinicianFilter || clinicFilter,
  );
  const unfilteredCount = data?.totalCount ?? data?.patients.length ?? 0;
  const baseCount =
    filter === "all" ? unfilteredCount : statusScopedPatients.length;
  const displayedCount = secondaryFiltersActive
    ? `${visiblePatients.length} of ${baseCount}`
    : `${baseCount}`;
  const refreshing =
    currentClinicianState.status === "refreshing" ||
    state.status === "refreshing" ||
    engagementState.status === "refreshing";
  const refresh = () => {
    currentClinicianState.refresh();
    if (adminConfirmed) {
      state.refresh();
      engagementState.refresh();
    }
  };

  const accessDenied = state.status === "error" && isAdminRequired(state.error);

  return (
    <ClinicianPortalLayout
      activeGlobalKey="admin-clients"
      clinicianCode={clinicianCode}
      therapistRole={currentClinicianData?.therapist?.role}
    >
      <PortalHeader
        title="Clients"
        clinicianName={
          adminConfirmed
            ? currentClinicianData?.therapist?.therapistName
            : undefined
        }
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[{ label: "Administration" }, { label: "Clients" }]}
          />
        }
      >
        {adminConfirmed ? (
          <div className="cp-filter-group" aria-label="Client status filter">
            <button
              type="button"
              aria-pressed={filter === "activated"}
              onClick={() => setFilter("activated")}
            >
              Activated Clients
            </button>
            <button
              type="button"
              aria-pressed={filter === "all"}
              onClick={() => setFilter("all")}
            >
              All Clients
            </button>
          </div>
        ) : null}
      </PortalHeader>
      <div className="cp-page-content cp-page-content--wide cp-page-content--sticky-list">
        {currentClinicianState.status === "loading" ||
        (adminConfirmed && state.status === "loading") ? (
          <LoadingState label="Loading clients" />
        ) : null}
        {currentClinicianState.status === "error" ? (
          <ErrorState
            error={currentClinicianState.error}
            onRetry={currentClinicianState.refresh}
          />
        ) : null}
        {accessDenied ? (
          <EmptyState
            title="Access denied"
            description="Administrator access is required to view all clients."
          />
        ) : state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {adminConfirmed && data ? (
          <section aria-label="Clients list">
            <div className="cp-content-header">
              <p>Review client records across the system.</p>
              <strong className="cp-record-count">
                {displayedCount} {baseCount === 1 ? "client" : "clients"}
              </strong>
            </div>
            <div className="cp-list-toolbar cp-list-toolbar--clients cp-list-toolbar--admin-clients">
              <div className="cp-list-toolbar__filters cp-list-toolbar__filters--admin-clients">
                <ClientListFilters
                  idPrefix="admin-clients"
                  search={search}
                  signal={signalFilter}
                  onSearchChange={setSearch}
                  onSignalChange={setSignalFilter}
                />
                <label className="cp-admin-client-list-filter">
                  <span className="cp-visually-hidden">
                    Filter by Clinician
                  </span>
                  <select
                    className="cp-form-input"
                    aria-label="Filter by Clinician"
                    value={clinicianFilter}
                    onChange={(event) =>
                      setClinicianFilter(event.currentTarget.value)
                    }
                  >
                    <option value="">All Clinicians</option>
                    {clinicianOptions.map((clinicianName) => (
                      <option key={clinicianName} value={clinicianName}>
                        {clinicianName}
                      </option>
                    ))}
                    {hasUnassignedClinician ? (
                      <option value={unassignedFilterValue}>Unassigned</option>
                    ) : null}
                  </select>
                </label>
                <label className="cp-admin-client-list-filter cp-admin-client-list-filter--clinic">
                  <span className="cp-visually-hidden">Filter by Clinic</span>
                  <select
                    className="cp-form-input"
                    aria-label="Filter by Clinic"
                    value={clinicFilter}
                    onChange={(event) =>
                      setClinicFilter(event.currentTarget.value)
                    }
                  >
                    <option value="">All Clinics</option>
                    {clinicOptions.map((clinic) => (
                      <option key={clinic.value} value={clinic.value}>
                        {clinic.label}
                      </option>
                    ))}
                    {hasUnassignedClinic ? (
                      <option value={unassignedFilterValue}>Unassigned</option>
                    ) : null}
                  </select>
                </label>
              </div>
              <div className="cp-list-toolbar__actions">
                <button
                  type="button"
                  className="cp-button cp-button--secondary"
                  onClick={refresh}
                  disabled={refreshing}
                >
                  <RefreshCw aria-hidden="true" />
                  {refreshing ? "Refreshing…" : "Refresh"}
                </button>
              </div>
            </div>
            {visiblePatients.length > 0 ? (
              <PortalDataTable
                caption="Clients"
                columns={columns}
                records={visiblePatients}
                getRecordKey={(patient) => patient.id}
              />
            ) : (
              <EmptyState
                title={
                  secondaryFiltersActive
                    ? "No matching clients"
                    : filter === "activated"
                      ? "No activated clients"
                      : "No clients"
                }
                description={
                  secondaryFiltersActive
                    ? "No clients match the current filters."
                    : filter === "activated"
                      ? "There are no activated clients to display. Try All Clients."
                      : "There are no clients in the system."
                }
              />
            )}
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
